import { useCallback } from 'react';
import { useSynchronousBindingLazy } from './synchronous.binding';

export enum ScratchpadExecutionBindingState {
  WAITING_TO_EXECUTE = 'waiting_to_execute',
  EXECUTION_COMPLETE = 'execution_complete',
  ERROR = 'error',
}

export interface ScratchpadExecutionBindingContext {
  data: Record<string, unknown>;
  disableEventPublishing: boolean;
}

export interface ScratchpadExecutionBindingResult {
  state: ScratchpadExecutionBindingState;
}

export type ScratchpadExecutionCallback = (
  result: ScratchpadExecutionBindingResult | undefined
) => void;

export interface ScratchpadExecutionBindingConfig {
  onResult?: ScratchpadExecutionCallback;
}

/**
 * Hook to bind scratchpad execution to a machine.
 * This will execute the scratchpad on the machine and provide updates on the execution state.
 * The binding is lazy - it won't subscribe until explicitly invoked.
 * The binding will remain open and continue to publish results until the component unmounts.
 */
export const useScratchpadExecutionBinding = ({
  onResult,
}: ScratchpadExecutionBindingConfig) => {
  const { send, close } = useSynchronousBindingLazy<
    ScratchpadExecutionBindingContext,
    ScratchpadExecutionBindingResult
  >({
    namespace: 'scratchpad',
    type: 'execution',
    closeImmediately: false,
    onResult: (result) => {
      onResult?.(result);

      // Close the binding when execution completes to allow a new one to be created
      if (
        result?.state === ScratchpadExecutionBindingState.EXECUTION_COMPLETE
      ) {
        close();
      }
    },
  });

  const execute = useCallback(
    async (machineId: string, context: ScratchpadExecutionBindingContext) => {
      return send(machineId, context);
    },
    [send]
  );

  return {
    execute,
    close,
  };
};
